/**
 * The renderer's modules, fetched while the home screen is read.
 *
 * Pixi loads its WebGL renderer and its browser extensions with `import()`
 * when it starts, so the first Play waited on the network: 0.5 s on fast 4G,
 * 5.3 s on slow 3G, over an empty screen (performance review, 2026-10-26,
 * problem 2). This asks for the same modules once the browser is idle after
 * the home screen is drawn, so they are in the cache by the time Play is
 * tapped. Fetching and evaluating them only registers Pixi's extensions, which
 * starting Pixi does anyway; no renderer is made.
 *
 * Pixi itself is not started on the home screen: on a hidden stage `resizeTo`
 * leaves the canvas at Pixi's 800×600 default (the same review).
 *
 * Production only. The dev server serves these files locally, and importing
 * a module behind its pre-bundled copy would load Pixi twice.
 */

let asked = false;

export function prefetchRenderer(): void {
  if (asked || !import.meta.env.PROD) return;
  asked = true;
  const fetchAll = (): void => {
    // The same module ids Pixi's own `import()`s resolve to, so the same chunks
    // (`pixi-webgl-renderer` is an alias in vite.config.ts). A failure is only a
    // missed head start: Play loads them as it always did. The renderer is
    // taken by name, as Pixi takes it: asking for the whole namespace made the
    // bundler fold the renderer into the first download, 139 kB more of it.
    import('pixi.js/browser').catch(() => {});
    import('pixi-webgl-renderer').then(
      ({ WebGLRenderer }) => WebGLRenderer,
      () => null,
    );
  };
  // After the first frame of the home screen, when nothing else is waiting.
  requestAnimationFrame(() => {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(fetchAll, { timeout: 3000 });
    else setTimeout(fetchAll, 1000);
  });
}
